// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useRef } from 'react';
import { useAnalytics } from './useAnalytics';
import { hashUserId } from '../utils/identity';

interface AnalyticsIdentityProps {
  /** Raw user ID (will be SHA-256 hashed before sending) */
  userId: string | undefined;
  /** User role (ADMIN, ADVISOR, CLIENT, SUPPORT, etc.) */
  role: string | undefined;
}

/**
 * Bridge component that connects auth context with analytics.
 * Place inside both AnalyticsProvider and the app's auth provider.
 * Hashes the user ID and calls setUser() when identity becomes available.
 * Renders nothing.
 */
export function AnalyticsIdentity({ userId, role }: AnalyticsIdentityProps) {
  const { setUser } = useAnalytics();
  const lastIdentified = useRef<string | null>(null);

  useEffect(() => {
    if (!userId || !role) return;

    // Avoid re-identifying the same user
    const key = `${userId}:${role}`;
    if (lastIdentified.current === key) return;

    let cancelled = false;

    hashUserId(userId).then((hashedId) => {
      if (!cancelled) {
        setUser(hashedId, role);
        lastIdentified.current = key;
      }
    });

    return () => {
      cancelled = true;
    };
  }, [userId, role, setUser]);

  return null;
}
